import React, { useMemo, useRef, useState } from 'react';
import { graphql } from 'gatsby';
import WithSection from '../../components/WithSection/WithSection';
import { Head2, Body1 } from '../../components/Typography/Typography';
import Box from '../../components/Box/Box';
import Image from '../../components/Image/Image';
import CtaSlots from '../../slots/CtaSlots/CtaSlots';
import { rem } from 'polished';
import { SECTION_VERTICAL_PADDING } from '../../components/SectionWrapper/SectionWrapper';
import { css } from 'styled-components';
import { getMode } from '../../utils/getMode';
import useViewport from '../../hooks/useViewport';
import ChevronRight from '../../images/icons/chevron-right.svg';
import { WatchAVideoButtonLink } from '../../components/WatchAVideoButtonLink/WatchAVideoButtonLink';
import Button from '../../components/Button/Button';
import { track } from '../../utils/analytics';
import BookingFormModal from '../../components/BookingFormModal/BookingFormModal';
import { getYoutubeIframeTag } from '../../utils/getYoutubeIframeTag';

const VersusHeroThemeColor = {
    light: {
        caption: 'primary.1500',
        heading: 'neutral.1800',
        subheading: 'neutral.1800',
    },
    dark: {
        caption: 'primary.200',
        heading: 'neutral.white',
        subheading: 'neutral.white',
    },
};

const VersusHeroSection = ({ data: { primary }, styling }) => {
    const {
        heading,
        subheading,
        cta,
        caption,
        image,
        video_embed_link,
        watch_a_video_button,
        watch_a_video_button_tracking_event_name,
    } = primary;
    const mode = getMode(styling.backgroundVariant);
    const colorsTokens = VersusHeroThemeColor[mode];
    const imageWrapperRef = useRef<HTMLDivElement>();
    const { isMobile } = useViewport();
    const imgElemHeight =
        imageWrapperRef.current
            ?.querySelector?.('img')
            ?.getBoundingClientRect?.()?.height || 500;
    const [isModalOpen, setIsModalOpen] = useState(false);

    return (
        <Box textAlign="center" gridColumn="1/-1">
            {caption && (
                <Body1 color={colorsTokens.caption} mb={rem(24)}>
                    {caption}
                </Body1>
            )}
            <Head2 bold color={colorsTokens.heading}>
                {heading.text}
            </Head2>
            {subheading && (
                <Body1 mt={rem(24)} color={colorsTokens.subheading}>
                    {subheading}
                </Body1>
            )}
            {cta?.document && (
                <Box
                    display="grid"
                    justifyContent={{ sm: 'center' }}
                    gridAutoFlow={{ sm: 'column' }}
                    whiteSpace={{ sm: 'nowrap' }}
                    gridGap="16px"
                    mt={rem(48)}
                >
                    <CtaSlots CTA={cta} bg={styling?.backgroundVariant} />
                    {watch_a_video_button &&
                        watch_a_video_button_tracking_event_name && (
                            <Button
                                size="lg"
                                variant="secondary"
                                endIcon={<ChevronRight />}
                                onClick={() => {
                                    setIsModalOpen(true);
                                    track(
                                        watch_a_video_button_tracking_event_name
                                    );
                                }}
                            >
                                {watch_a_video_button}
                            </Button>
                        )}
                </Box>
            )}
            <Box
                hidden={isMobile}
                position="absolute"
                bottom={0}
                left={0}
                width="100%"
                height={imgElemHeight / 2 + SECTION_VERTICAL_PADDING}
                bg="neutral.white"
            />
            <Box mt={rem(32)} width="100%">
                <Image
                    ref={imageWrapperRef}
                    img={image}
                    css={css`
                        border-radius: ${({ theme }) => theme.radii.sm};
                    `}
                />
            </Box>
            {isModalOpen && video_embed_link.url && (
                <BookingFormModal onClose={() => setIsModalOpen(false)}>
                    <BookingFormModal.FrameContainer
                        onClose={() => setIsModalOpen(false)}
                        dangerouslySetInnerHTML={{
                            __html: getYoutubeIframeTag(video_embed_link.url),
                        }}
                    />
                </BookingFormModal>
            )}
        </Box>
    );
};

export const _ = graphql`
    fragment VERSUS_HERO_SECTION on PrismicSectionDataBodyVersusHeroSection {
        id
        primary {
            caption
            heading {
                text
            }
            subheading
            watch_a_video_button
            watch_a_video_button_tracking_event_name
            video_embed_link {
                url
            }
            image {
                gatsbyImageData(layout: CONSTRAINED, height: 500)
                alt
                url
            }
            cta {
                document {
                    ...CTA_FRAGMENT
                }
            }
        }
    }
`;

export default WithSection(VersusHeroSection);
